<script lang="ts" setup>
import { useFullscreen } from '@vueuse/core'
import { useTemplateRef, watch, ref } from 'vue'
import Countdown from '../atoms/Countdown.vue'
import Loader from '../atoms/Loader.vue'

export interface Props {
  message?: string
  qr?: string
  more?: string
  t?: (key?: string) => string
  showLoader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
  qr: '',
  more: '',
  t: () => '',
  showLoader: true,
})

const toggleQr = useTemplateRef<HTMLElement>('toggleQr')
const { toggle } = useFullscreen(toggleQr)

// Track whether the QR code has ever been shown so that Countdown stays
// mounted even when `qr` is transiently empty between poll cycles.
// This prevents the countdown timer from restarting on every poll.
const hasShownQr = ref(Boolean(props.qr))
watch(
  () => props.qr,
  val => {
    if (val) hasShownQr.value = true
  },
)
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-white">
    <div
      :class="[
        'flex flex-col items-center',
        showLoader ? '' : 'top-0 rounded-xl bg-blue-lighter px-12 pb-12 pt-8',
      ]"
    >
      <Loader v-if="showLoader" />
      <h2
        :class="[
          'mt-2 text-center font-serif text-2xl font-bold leading-none lg:text-5xl lg:leading-snug',
          showLoader ? 'text-blue' : 'text-black',
        ]"
        :level="2"
      >
        {{ message || t('spinner.message') }}
      </h2>
      <p v-if="more" class="mt-2 text-center font-sans text-base text-black lg:text-xl">
        {{ more }}
      </p>
      <div v-if="qr || hasShownQr">
        <img
          v-if="qr"
          ref="toggleQr"
          :src="qr"
          alt="QR-kod"
          role="button"
          tabindex="0"
          class="mt-4 h-64 w-64"
          @click="toggle"
          @keydown.enter.prevent="toggle"
          @keydown.space.prevent="toggle"
        />
        <p v-if="qr" class="mt-4 text-sm">Klicka på QR-koden för att förstora den</p>
        <!-- Countdown is outside v-if="qr" so it keeps running even when the QR
           value is transiently null between polls, preventing the timer from
           restarting on every poll cycle. -->
        <Countdown class="font-semibold" />
      </div>
    </div>
  </div>
</template>
